import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";

interface PinDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (action: string, pin: string) => void;
    fullName: string;
    currentStatus: string;
}

export const PinDialog: React.FC<PinDialogProps> = ({ open, onClose, onSubmit, fullName, currentStatus }) => {
    const [pin, setPin] = useState("");
    const [action, setAction] = useState("");
    const [pinStep, setPinStep] = useState(0); //if 0 clock in, clock out start break, end break will show up, if 1 then pin dialog shows

    // Reset PIN whenever the dialog closes
    useEffect(() => {
        if (!open) {
            resetState()
        }
    }, [open]);

    const handleNumberClick = (num: string) => {
        setPin((prev) => prev + num);
    };

    const handleDelete = () => {
        setPin((prev) => prev.slice(0, -1));
    };

    const handleSubmit = () => {
        onSubmit(action, pin);
        onClose()
        resetState()
    };

    const resetState =() => {
        setPin("")
        setPinStep(0)
        setAction("")
    }

    const setActionText = (text: string) => {
        setAction(text)
        setPinStep(1)
    }

    const getActionClassName = (actionName: string): string => {
        const recommendedAction =
            (currentStatus === "Clocked Out" && actionName === "Clock In") ||
            (currentStatus === "On Break" && actionName === "Get Back to Work");

        return recommendedAction ? "pin-action-button recommended" : "pin-action-button";
    };

    const renderSteps = () => {
        if (pinStep === 0) {
            return (
                <div className="pin-action-container">
                    <button className={getActionClassName("Clock In")} onClick={() => setActionText("Clock In")}>
                        <span className="action-icon clock-in-icon" aria-hidden="true">→</span>
                        <span><strong>Clock in</strong><small>Start my shift</small></span>
                    </button>
                    <button className={getActionClassName("Take a Break")} onClick={() => setActionText("Take a Break")}>
                        <span className="action-icon break-icon" aria-hidden="true">Ⅱ</span>
                        <span><strong>Take a break</strong><small>Pause my shift</small></span>
                    </button>
                    <button className={getActionClassName("Get Back to Work")} onClick={() => setActionText("Get Back to Work")}>
                        <span className="action-icon resume-icon" aria-hidden="true">▶</span>
                        <span><strong>End break</strong><small>Get back to work</small></span>
                    </button>
                    <button className={getActionClassName("Clock Out")} onClick={() => setActionText("Clock Out")}>
                        <span className="action-icon clock-out-icon" aria-hidden="true">←</span>
                        <span><strong>Clock out</strong><small>Finish my shift</small></span>
                    </button>
                </div>
            )
        }
        if (pinStep === 1) {
            return (
                <div className="pin-step-container">
                    <p className="pin-instruction">Enter your PIN to confirm</p>
                    <div className="pin-input" aria-label={`${pin.length} PIN digits entered`}>
                        {pin.length > 0 ? "•".repeat(pin.length) : <span>PIN</span>}
                    </div>
                    <div className="number-pad">

                        {"123456789".split("").map((num) => (
                            <button
                                key={num}
                                className="number-button"
                                onClick={() => handleNumberClick(num)}
                            >
                                {num}
                            </button>
                        ))}
                        <button className="number-button utility-button" onClick={handleDelete} aria-label="Delete last digit">⌫</button>
                        <button
                            className="number-button"
                            onClick={() => handleNumberClick("0")}
                        >
                            0
                        </button>
                        <button className="number-button submit-pin-button" onClick={handleSubmit} disabled={pin.length === 0} aria-label="Submit PIN">✓</button>
                    </div>
                    <button className="back-to-actions" onClick={() => { setPin(""); setPinStep(0); }}>
                        ← Choose a different action
                    </button>
                </div>
            )
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            className="kiosk-pin-dialog"
        >
            <div className="pin-dialog-header">
                <div>
                    <span className="pin-dialog-label">Time clock</span>
                    <h2 className="pin-header-name">{fullName}</h2>
                    <span className={`status-badge ${currentStatus.toLowerCase().replaceAll(" ", "-").replace("…", "")}`}>{currentStatus}</span>
                </div>
                <button className="close-pin-dialog" onClick={onClose} aria-label="Close">×</button>
            </div>
            {action && <h3 className="pin-header-action">{action}</h3>}
            <DialogContent className="pin-dialog">
                {renderSteps()}
            </DialogContent>
        </Dialog>
    );
};
require('./styles.scss')
